import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ListChecks, Plus, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = [
  { code: "diploma",  name: "Diploma",  numeric_rank: 4 },
  { code: "bachelor", name: "Bachelor", numeric_rank: 6 },
  { code: "master",   name: "Master",   numeric_rank: 7 },
  { code: "doctoral", name: "Doctoral", numeric_rank: 8 },
];

/**
 * Per-framework qualification-levels editor. Used from the Frameworks
 * management page. Levels are stored on the framework document; each
 * level needs a unique `code`, a display `name`, and a `numeric_rank`
 * 1-8 that determines badge templates & ordering.
 */
export default function FrameworkLevelsDialog({ framework, onSaved }) {
  const [open, setOpen] = useState(false);
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLevels(framework?.levels?.length ? [...framework.levels] : [...DEFAULTS]);
  }, [open, framework]);

  const updateRow = (i, key, value) => {
    setLevels((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };
  const addRow = () => setLevels((rows) => [...rows, { code: "", name: "", numeric_rank: rows.length + 1 }]);
  const removeRow = (i) => setLevels((rows) => rows.filter((_, idx) => idx !== i));

  const save = async () => {
    // Client-side validation matches the server: code unique, name & code required.
    const seen = new Set();
    for (const row of levels) {
      const code = (row.code || "").trim().toLowerCase();
      const name = (row.name || "").trim();
      if (!code || !name) {
        toast.error("Every row needs both a code and a name.");
        return;
      }
      if (seen.has(code)) {
        toast.error(`Duplicate code: ${code}`);
        return;
      }
      seen.add(code);
    }
    setSaving(true);
    try {
      const payload = {
        levels: levels.map((r) => ({
          code: (r.code || "").trim().toLowerCase(),
          name: (r.name || "").trim(),
          numeric_rank: Number(r.numeric_rank) || 1,
        })),
      };
      await axios.put(`${API}/frameworks/${framework.framework_id}/levels`, payload, {
        withCredentials: true,
      });
      toast.success(`Levels updated for ${framework.name}.`);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          title="Manage Qualification Levels"
          data-testid={`framework-levels-${framework.framework_id}`}
        >
          <ListChecks className="w-3.5 h-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Qualification Levels — {framework.name}</DialogTitle>
          <DialogDescription>
            Each level represents an outcome this framework can issue
            (e.g. Diploma, Bachelor, MQA Level 4, SWEP Master).
            <strong> Code</strong> is the internal identifier (lowercase, unique);
            <strong> Name</strong> is what candidates see;
            <strong> Rank</strong> (1-8) drives badge templates and ordering.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-2">
            <div className="col-span-3">Code</div>
            <div className="col-span-6">Name</div>
            <div className="col-span-2">Rank</div>
            <div className="col-span-1" />
          </div>
          {levels.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input
                className="col-span-3 font-mono text-sm"
                placeholder="diploma"
                value={row.code || ""}
                onChange={(e) => updateRow(i, "code", e.target.value)}
                data-testid={`level-row-${i}-code`}
              />
              <Input
                className="col-span-6"
                placeholder="Diploma"
                value={row.name || ""}
                onChange={(e) => updateRow(i, "name", e.target.value)}
                data-testid={`level-row-${i}-name`}
              />
              <Input
                className="col-span-2 text-center"
                type="number"
                min={1}
                max={20}
                value={row.numeric_rank ?? 1}
                onChange={(e) => updateRow(i, "numeric_rank", Number(e.target.value))}
                data-testid={`level-row-${i}-rank`}
              />
              <Button
                variant="ghost"
                size="sm"
                className="col-span-1 h-8 w-8 p-0 text-rose-500"
                onClick={() => removeRow(i)}
                title="Remove level"
                data-testid={`level-row-${i}-remove`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-level-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add level
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="save-levels-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save levels
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
